import {combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {mergeMap, map} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {AbstractControlComponent} from '../abstract-control';
import {ViewTabComponent} from '../../viewtab.component';
import {ErrorService} from '../../../../error/error.service';
import * as convert from 'xml-js';
import {CacheService} from '../../../../cache/cache.service';
import {LanguageService} from '../../../../language/language.service';
import {ResourceService} from '../../../resource.service';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {
  filterAttributesFromDsml,
  getSingleXMLTagValue,
  parseAttributesFromManagementAgent,
} from './SynchronizationFunctions';

@Component({
  selector: 'ctrl-relationship-criteria',
  styleUrls: ['./ctrl-synchronization.scss'],
  template: `
    <table class="table mat-elevation-z8">
      <thead>
        <tr>
          <th>
            <language
              key="CtrlRelationshipCriteriaColumnIlmAttribute"
              defaultValue="Metaverse Object Attribute"
            ></language>
          </th>
          <th class="text-center">=</th>
          <th>
            <language
              key="CtrlRelationshipCriteriaColumnCsAttribute"
              defaultValue="Connected System Object Attribute"
            ></language>
          </th>
          <th class="buttons">
            <button
              mat-icon-button
              (click)="addRow()"
              color="accent"
              [disabled]="(getCaughtObservable(isEnabled) | async) != true"
            >
              <mat-icon arial-label="Add">add</mat-icon>
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        @for (entry of formControl?.value; track entry; let i = $index) {
          <tr class="entry">
            <td>
              <mat-form-field>
                <mat-select
                  [(ngModel)]="entry.ilmAttribute"
                  (selectionChange)="formControl.updateValueAndValidity()"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  [placeholder]="ilmAttributePlaceholder | async"
                >
                  @for (attr of ilmAttributes | async; track attr) {
                    <mat-option [value]="attr">{{ attr }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </td>
            <td class="text-center">=</td>
            <td>
              <mat-form-field>
                <mat-select
                  [(ngModel)]="entry.csAttribute"
                  (selectionChange)="formControl.updateValueAndValidity()"
                  [disabled]="(getCaughtObservable(isEnabled) | async) != true"
                  [placeholder]="csAttributePlaceholder | async"
                >
                  @for (attr of csAttributes | async; track attr) {
                    <mat-option [value]="attr">{{ attr }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </td>
            <td class="buttons">
              <button
                mat-icon-button
                (click)="delete(i)"
                [disabled]="(getCaughtObservable(isEnabled) | async) != true"
              >
                <mat-icon aria-label="Delete">close</mat-icon>
              </button>
            </td>
          </tr>
        }
      </tbody>
    </table>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlRelationshipCriteriaComponent extends AbstractControlComponent {
  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private cacheService: CacheService,
    private resourceService: ResourceService,
    private envConfigService: EnvironmentConfigService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, _ngZone, errorService);
    this.csAttributePlaceholder = this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlRelationshipCriteriaCsAttributePlaceholder',
          'Please select an item.',
          lr,
        );
      }),
    );
    this.ilmAttributePlaceholder = this.cacheService.getLanguageResource().pipe(
      map(lr => {
        return LanguageService.translate(
          'CtrlRelationshipCriteriaIlmAttributePlaceholder',
          'Please select an item.',
          lr,
        );
      }),
    );
  }

  csAttributePlaceholder: Observable<string>;
  ilmAttributePlaceholder: Observable<string>;
  csAttributes: Observable<string[]>;
  ilmAttributes: Observable<string[]>;

  protected formatSource(source: any): any {
    // <conditions><condition><ilmAttribute>cn</ilmAttribute><csAttribute>displayName</csAttribute></condition></conditions>
    if (source == null || typeof source !== 'string' || source.length === 0) {
      return [];
    }

    let result: RelationshipCriteriaCondition[] = [];

    const obj: any = convert.xml2js(source, {
      compact: true,
      alwaysArray: true,
    });

    if (obj == null || !Array.isArray(obj.conditions) || obj.conditions.length !== 1) {
      throw new Error('Failed converting UocRelationshipCriteriaControl Source XML to JS');
    }

    if (Array.isArray(obj.conditions[0].condition)) {
      obj.conditions[0].condition.forEach(xmlScope => {
        const condition = new RelationshipCriteriaCondition();
        condition.ilmAttribute = getSingleXMLTagValue(xmlScope, 'ilmAttribute');
        condition.csAttribute = getSingleXMLTagValue(xmlScope, 'csAttribute');
        result.push(condition);
      });
    }

    if (result.length === 0) {
      result.push(new RelationshipCriteriaCondition());
    }

    return result;
  }

  protected formatTarget(value: any): any {
    const nullValue = '<conditions/>';

    if (!Array.isArray(value)) {
      return nullValue;
    }

    value = value.filter(
      (rcc: RelationshipCriteriaCondition) => rcc.csAttribute != null && rcc.ilmAttribute != null,
    );
    if (value.length === 0) {
      return nullValue;
    }

    const obj = {
      conditions: {
        condition: [],
      },
    };

    value.forEach((val: RelationshipCriteriaCondition) => {
      if (!(val instanceof RelationshipCriteriaCondition)) {
        return;
      }

      const condition = {
        ilmAttribute: undefined,
        csAttribute: undefined,
      };

      if (val.ilmAttribute != null) {
        condition.ilmAttribute = {_text: val.ilmAttribute};
      }

      if (val.csAttribute != null) {
        condition.csAttribute = {_text: val.csAttribute};
      }

      obj.conditions.condition.push(condition);
    });

    const xml = convert.js2xml(obj, {
      compact: true,
    });

    return xml;
  }

  protected initValidators(): void {
    this.csAttributes = this._config
      .getPropertyValues('ManagementAgent')
      .pipe(
        mergeMap(id =>
          parseAttributesFromManagementAgent(id, this.resourceService, this.envConfigService),
        ),
      );

    this.ilmAttributes = observableCombineLatest(
      this._config.getPropertyValues('MVSchema'),
      this._config.getPropertyValues('MVObjectType'),
      filterAttributesFromDsml,
    );
  }

  delete(index: number): void {
    this.formControl.value.splice(index, 1);
    this.formControl.updateValueAndValidity();
  }

  addRow(): void {
    // don't fire event - unnecessary
    if (!Array.isArray(this.formControl.value)) {
      this.formControl.setValue([new RelationshipCriteriaCondition()], {emitEvent: false});
    } else {
      this.formControl.value.push(new RelationshipCriteriaCondition());
    }
  }
}

class RelationshipCriteriaCondition {
  ilmAttribute: string;
  csAttribute: string;
}
